/**
 * Milestone + thermometer math — pure, read-side, derived entirely from the
 * cents a school has accumulated on the current board. No backend state: these
 * are a display layer over the contribution total (points stay 1:1 with dollars
 * per the games design), so a reversal that lowers cents automatically lowers
 * the milestone.
 */

export interface Milestone {
  cents: number;
  label: string;
  /** Emoji medal shown on the badge once this milestone is crossed. */
  medal: string;
}

/** Ascending milestone tiers. Extend here; the UI derives everything from this. */
export const MILESTONES: Milestone[] = [
  { cents: 100_000, label: '$1k', medal: '🥉' },
  { cents: 500_000, label: '$5k', medal: '🥈' },
  { cents: 1_000_000, label: '$10k', medal: '🥇' },
];

export interface MilestoneProgress {
  /** Highest milestone reached, or null if none crossed yet. */
  reached: Milestone | null;
  /** Next milestone to aim for, or null once the top tier is crossed. */
  next: Milestone | null;
  /** Fraction [0,1] toward `next` from the previous tier (1 when maxed out). */
  fraction: number;
}

/**
 * Resolve a school's milestone standing from its contribution cents. `fraction`
 * runs from the previous tier's floor to the next tier's target, so the
 * thermometer fills within the current band rather than always relative to zero.
 */
export function milestoneProgress(cents: number): MilestoneProgress {
  const reached =
    [...MILESTONES].reverse().find((m) => cents >= m.cents) ?? null;
  const next = MILESTONES.find((m) => cents < m.cents) ?? null;
  if (!next) return { reached, next: null, fraction: 1 };
  const floor = reached ? reached.cents : 0;
  const span = next.cents - floor;
  const fraction = span > 0 ? Math.max(0, Math.min(1, (cents - floor) / span)) : 0;
  return { reached, next, fraction };
}

/** The top (largest) milestone — the full range the ladder bar spans to. */
export const TOP_MILESTONE: Milestone = MILESTONES[MILESTONES.length - 1];

/** A tier tick positioned along the full 0->top-tier ladder bar. */
export interface MilestoneTick extends Milestone {
  /** Position along the full range as a percentage [0,100]. */
  positionPct: number;
  /** True once the school has reached this tier (tick renders lit). */
  reached: boolean;
}

export interface LadderProgress extends MilestoneProgress {
  /** Fill across the WHOLE ladder (0 -> top tier), clamped [0,1]. */
  ladderFraction: number;
  /** Each tier as a tick along the full bar, with position + lit state. */
  ticks: MilestoneTick[];
}

/**
 * Full-range ladder view (Option A): the bar spans 0 -> top tier, the fill is the
 * school's position across that whole range, and each tier is a tick at its real
 * position. Reuses milestoneProgress for reached/next/next-band `fraction` (kept
 * for the "$X to $Y" caption).
 */
export function ladderProgress(cents: number): LadderProgress {
  const base = milestoneProgress(cents);
  const top = TOP_MILESTONE.cents;
  const ladderFraction = top > 0 ? Math.max(0, Math.min(1, cents / top)) : 0;
  const ticks: MilestoneTick[] = MILESTONES.map((m) => ({
    ...m,
    positionPct: (m.cents / top) * 100,
    reached: cents >= m.cents,
  }));
  return { ...base, ladderFraction, ticks };
}
